<script setup lang="ts">
import type { FuzzyResult } from "ls/features/command-k/types";
import type { ExcludeType } from "ls/features/workspaces/destinationSuggestionsTypes";
import type { Component } from "vue";
import type { RecentJs, SearchShortcutJs, ShortcutJs } from "@/generated/models";
import { createReusableTemplate } from "@vueuse/core";
import { useLocalSearch } from "ls/features/command-k/useLocalSearch";
import FileIcon from "ls/features/files/FileIcon.vue";
import { toFileOverview } from "ls/router/builders";
import { shortcutParameterName } from "ls/router/constants";
import { GlobeIcon, LoaderCircleIcon } from "lucide-vue-next";
import { hasProtocol, joinURL, withoutBase, withoutTrailingSlash, withProtocol } from "ufo";
import { computed, ref, toRaw, watch } from "vue";
import { useRouter } from "vue-router";
import { getRouteParam } from "@/router/utils";
import { useEmbedLinkSuggestions } from "./useEmbedLinkSuggestions";

type SuggestionItemType = RecentJs | FuzzyResult<SearchShortcutJs> | string;

const props = withDefaults(defineProps<{
  searchTerm?: string;
  exclude?: ExcludeType[];
  filterFiles?: (file: ShortcutJs) => boolean;
  shortcut: string;
}>(), {
  searchTerm: () => "",
  exclude: () => [],
  filterFiles: () => true,
});

const emit = defineEmits<{
  select: [string];
}>();

const suggested = defineModel<string | undefined>();

const router = useRouter();
const recentSuggestions = useEmbedLinkSuggestions({ shortcut: () => props.shortcut, limit: 8, sameWorkspaceLimit: 4, filterFiles: props.filterFiles });

const filesSearchTerm = computed(() => {
  if (isValidUrl(props.searchTerm)) {
    try {
      const url = new URL(props.searchTerm);
      if (url.origin !== globalThis.location.origin) {
        return props.searchTerm;
      }
      const relativePath = withoutBase(url.pathname + url.search + url.hash, import.meta.env.VITE_APP_BASE_SUFFIX);
      const result = router.resolve(relativePath);
      const shortcut = getRouteParam(result.params, shortcutParameterName);
      if (!shortcut) {
        return props.searchTerm;
      }
      return shortcut;
    } catch {
      return props.searchTerm;
    }
  }
  return props.searchTerm;
});

const excludeViewOnly = computed(() => props.exclude.includes("view-only"));
const excludeArchived = computed(() => props.exclude.includes("archived"));
const excludePrivate = computed(() => props.exclude.includes("private-workspace"));
const { files, isPending } = useLocalSearch(filesSearchTerm, { limit: 25, excludeViewOnly, excludeArchived, excludePrivate });
const filteredFiles = computed(() => files.value.filter(file => props.filterFiles(file.Shortcut)));

const items = computed(() => {
  if (!props.searchTerm) {
    return recentSuggestions.value;
  }
  const urlToCheck = ensureProtocol(props.searchTerm);
  if (!isValidUrl(urlToCheck)) {
    return filteredFiles.value;
  }
  return [...filteredFiles.value, urlToCheck];
});

const noResults = computed(() => !items.value || items.value.length === 0);

const highlightedItem = ref<SuggestionItemType | undefined>();
const highlightedItemIndex = ref<number>(-1);
const itemRefs = ref<Record<number, Element>>({});

watch(() => props.searchTerm, () => {
  setHighlightedItem(undefined);
});

function isValidUrl(value: string) {
  if (!value) return false;
  return URL.canParse(value);
}

function ensureProtocol(value: string) {
  if (hasProtocol(value)) {
    return value;
  } else {
    return withProtocol(value, "https://");
  }
}

function getShortcutUrl(shortcut: ShortcutJs) {
  const route = router.resolve(toFileOverview(shortcut));
  return joinURL(globalThis.location.origin, route.href);
}

function select(value: string) {
  if (!value) return;
  emit("select", ensureProtocol(value));
}

function handleSelect(value: SuggestionItemType) {
  if (typeof value === "string") {
    select(value);
  } else {
    select(getShortcutUrl(value.Shortcut));
  }
}

function handleHighlight(value: SuggestionItemType | undefined) {
  if (!value) {
    suggested.value = undefined;
  } else {
    let url: string;
    if (typeof value === "string") {
      url = value;
    } else {
      url = getShortcutUrl(value.Shortcut);
    }
    url = ensureProtocol(url);
    suggested.value = url;
  }
}

function setHighlightedItem(value: SuggestionItemType | undefined, scrollIntoView: boolean = true) {
  if (toRaw(highlightedItem.value) === toRaw(value)) return;

  highlightedItem.value = value;
  if (value) {
    const index = items.value.findIndex(f => f === value);
    highlightedItemIndex.value = index;
    if (index >= 0 && scrollIntoView) {
      itemRefs.value[index]?.scrollIntoView({ block: "nearest" });
    }
  } else {
    highlightedItemIndex.value = -1;
  }
  handleHighlight(value);
}

function highlightFirst() {
  if (noResults.value) return;
  setHighlightedItem(items.value[0]);
}

function highlightLast() {
  if (noResults.value) return;
  setHighlightedItem(items.value[items.value.length - 1]);
}

function highlightNext() {
  if (noResults.value) return;

  if (!highlightedItem.value) {
    highlightFirst();
    return;
  }
  const currentIndex = highlightedItemIndex.value;
  if (currentIndex < 0 || currentIndex >= items.value.length - 1) {
    return;
  }
  setHighlightedItem(items.value[currentIndex + 1]);
}

function highlightPrevious() {
  if (noResults.value) return;

  if (!highlightedItem.value) {
    highlightLast();
    return;
  }
  const currentIndex = highlightedItemIndex.value;
  if (currentIndex <= 0) {
    return;
  }
  setHighlightedItem(items.value[currentIndex - 1]);
}

defineExpose({
  highlightNext,
  highlightPrevious,
});

const [DefineTemplate, ReuseTemplate] = createReusableTemplate<{
  name: string;
  path: string;
}, { icon: Component }>();
</script>

<template>
  <DefineTemplate v-slot="{ $slots, name, path }">
    <div class="flex min-w-0 flex-1 flex-row items-center gap-2">
      <component :is="$slots.icon" />
      <div class="overflow-hidden">
        <p class="truncate">
          {{ name }}
        </p>
        <div v-if="path && path.trim() !== '/'" class="truncate text-xs text-slate-500">
          {{ withoutTrailingSlash(path) }}
        </div>
      </div>
    </div>
  </DefineTemplate>
  <div class="relative flex min-h-0 flex-col overflow-auto">
    <div class="mt-1 px-1 text-xs font-semibold text-slate-500">
      Suggested
    </div>
    <div
      class="flex min-h-0 flex-col"
      @pointerleave="setHighlightedItem(undefined)"
      @focusout="setHighlightedItem(undefined)"
      @keydown.down.prevent="highlightNext()"
      @keydown.up.prevent="highlightPrevious()"
    >
      <div v-if="noResults && !isPending" class="flex flex-col items-center">
        <div
          class="py-2 text-xs text-slate-500 select-none"
        >
          Nothing to show here
        </div>
      </div>
      <div
        v-for="(item, index) in items"
        v-else
        :key="index"
        :ref="(el: any) => itemRefs[index] = el"
        class="rounded-sm px-2 py-1 focus:outline-hidden"
        tabindex="0"
        :class="[{ 'bg-gray-200/40': toRaw(highlightedItem) === toRaw(item) }]"
        @click="handleSelect(item)"
        @keydown.enter.prevent="handleSelect(item)"
        @focus="setHighlightedItem(item)"
        @pointermove="setHighlightedItem(item, false)"
      >
        <div
          v-if="typeof item === 'string'"
          class="flex min-w-0 flex-1 flex-row items-center gap-2"
        >
          <slot name="recognized-url">
            <GlobeIcon class="size-4 shrink-0" aria-hidden="true" />
            <div class="flex flex-col">
              <span>Embed URL</span>
              <span class="text-xs text-slate-500">Some websites may block embedding due to security restrictions</span>
            </div>
          </slot>
        </div>
        <ReuseTemplate
          v-else
          :name="item.Shortcut.Name"
          :path="item.Path"
        >
          <template #icon>
            <FileIcon :file="item.Shortcut" class="size-4 shrink-0" aria-hidden="true" />
          </template>
        </ReuseTemplate>
      </div>
      <div
        v-if="isPending"
        class="flex h-9 min-w-0 flex-col items-center justify-center"
      >
        <LoaderCircleIcon
          class="col-start-1 row-start-1 size-4 animate-spin"
        />
      </div>
    </div>
  </div>
</template>
